import { Dispatch, SetStateAction, useEffect } from 'react';
import { useFirstMountState, usePrevious } from 'react-use';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { MarketId, UIMarket } from '@drift/common';
import { BigNum } from '@drift-labs/sdk';
import {
	SaveTpSlOrdersCallback,
	TpSlInputType,
	TpSlTriggerPercentBasis,
	TpSlInputSource,
} from 'src/components/TradeForm/TpSlOrderForm/types';
import { TpSlOrderFormState } from 'src/components/TradeForm/TpSlOrderForm/hooks/useTpSlOrderFormState';
import { validateTpSlOrder } from 'src/utils/tpSlValidation';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';
import useGetTickSizeForMarket, {
	getTickSizeDecimalPlaces,
} from 'src/hooks/useGetTickSizeForMarket';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';

interface Params {
	direction: 'long' | 'short';
	selectedMarketId: MarketId | undefined;
	triggerPercentBasis: TpSlTriggerPercentBasis;
	saveOrdersCallback: SaveTpSlOrdersCallback;
	updateOrders: () => void;
	clearTakeProfitState: () => void;
	clearStopLossState: () => void;
	calcPercent: (price: BigNum) => number;
	calcPrice: (percentMove: number, isTakeProfit: boolean) => BigNum;
	setTakeProfitState: React.Dispatch<React.SetStateAction<TpSlOrderFormState>>;
	setStopLossState: React.Dispatch<React.SetStateAction<TpSlOrderFormState>>;
	takeProfitPrice: BigNum | null;
	stopLossPrice: BigNum | null;
	takeProfitInputSource: TpSlInputSource;
	stopLossInputSource: TpSlInputSource;
	takeProfitLimitPrice: BigNum | null;
	stopLossLimitPrice: BigNum | null;
	entryPrice: BigNum;
	markPrice: BigNum;
	allowTrailingStopLoss: boolean;
}

export default function useTpSlOrderFormSideEffects({
	direction,
	selectedMarketId,
	triggerPercentBasis,
	saveOrdersCallback,
	updateOrders,
	clearTakeProfitState,
	clearStopLossState,
	calcPercent,
	calcPrice,
	setTakeProfitState,
	setStopLossState,
	takeProfitPrice,
	stopLossPrice,
	takeProfitInputSource,
	stopLossInputSource,
	takeProfitLimitPrice,
	stopLossLimitPrice,
	entryPrice,
	markPrice,
	allowTrailingStopLoss,
}: Params) {
	const appEventEmitter = useAppEventEmitter();
	const isTpSlCheckboxChecked = useDriftStore(
		(s) => s.tradeForm.isTpSlCheckboxChecked
	);

	// When the user unchecks the TpSL checkbox / hides the form, we do not clear
	// their filled in values, in case they decide to enable TpSl orders again.
	// However, we do clear their orders from the trade form state so they are
	// not submitted. So, when the checkbox is checked again, we need to re-save
	// their orders.
	useEffect(() => {
		if (isTpSlCheckboxChecked) {
			updateOrders();
		} else {
			saveOrdersCallback(undefined);
		}
	}, [isTpSlCheckboxChecked, updateOrders, saveOrdersCallback]);

	////////////////////////////////////////////////////////////////////////////
	// Reset Form Effects
	////////////////////////////////////////////////////////////////////////////

	// Reset the form when specified deps have changed.
	const isFirstMount = useFirstMountState();
	useEffect(() => {
		if (isFirstMount) {
			return;
		}

		clearStopLossState();
		clearTakeProfitState();
		saveOrdersCallback(undefined);
	}, [direction, selectedMarketId?.key]);

	// Reset inputs when trade is confirmed.
	useEffect(() => {
		const clearInputs = () => {
			clearStopLossState();
			clearTakeProfitState();
			saveOrdersCallback(undefined);
		};

		appEventEmitter.on('tradeConfirmation', clearInputs);

		return () => {
			appEventEmitter.removeListener('tradeConfirmation', clearInputs);
		};
	}, [appEventEmitter]);

	////////////////////////////////////////////////////////////////////////////

	////////////////////////////////////////////////////////////////////////////
	// When `triggerPercentBasis` changes, recalculate percentage values based on
	// existing prices.
	////////////////////////////////////////////////////////////////////////////

	useEffect(() => {
		if (isFirstMount) {
			return;
		}

		const tpPriceForPercentCalc = (() => {
			if (takeProfitLimitPrice && !takeProfitLimitPrice.eqZero()) {
				return takeProfitLimitPrice;
			}

			if (takeProfitPrice && !takeProfitPrice.eqZero()) {
				return takeProfitPrice;
			}
		})();

		// Recalculate take profit percentage if price exists (preserve existing price values)
		if (tpPriceForPercentCalc) {
			const newTakeProfitPercent = calcPercent(tpPriceForPercentCalc);

			setTakeProfitState((prevState) => ({
				...prevState,
				percent: newTakeProfitPercent,
				percentStringValue: newTakeProfitPercent.toFixed(2),
			}));
		}

		const slPriceForPercentCalc = (() => {
			if (stopLossLimitPrice && !stopLossLimitPrice.eqZero()) {
				return stopLossLimitPrice;
			}

			if (stopLossPrice && !stopLossPrice.eqZero()) {
				return stopLossPrice;
			}
		})();

		// Recalculate stop loss percentage if price exists (preserve existing price values)
		if (slPriceForPercentCalc) {
			const newStopLossPercent = calcPercent(slPriceForPercentCalc);

			setStopLossState((prevState) => ({
				...prevState,
				percent: newStopLossPercent,
				percentStringValue: newStopLossPercent.toFixed(2),
			}));
		}

		// Update orders with recalculated values
		updateOrders();
	}, [triggerPercentBasis]);

	////////////////////////////////////////////////////////////////////////////

	////////////////////////////////////////////////////////////////////////////
	// Clear TP/SL when leverage changes.
	////////////////////////////////////////////////////////////////////////////

	const marketSymbol = UIMarket.createPerpMarket(
		selectedMarketId?.marketIndex ?? 0
	).symbol;
	const savedMaxLev = useMarketSettingsStore((s) =>
		s.getMaxLeverageForMarket(marketSymbol)
	);
	const prevSavedMaxLev = usePrevious(savedMaxLev);

	useEffect(() => {
		if (prevSavedMaxLev !== undefined && prevSavedMaxLev !== savedMaxLev) {
			clearTakeProfitState();
			clearStopLossState();
		}
	}, [savedMaxLev, prevSavedMaxLev, clearTakeProfitState, clearStopLossState]);

	////////////////////////////////////////////////////////////////////////////

	////////////////////////////////////////////////////////////////////////////
	// Auto-update the appropriate input when the entry price changes.
	////////////////////////////////////////////////////////////////////////////

	const getTickSizeForMarket = useGetTickSizeForMarket();
	const tickSize = getTickSizeForMarket(selectedMarketId);
	const tickSizeDecimalPlaces = getTickSizeDecimalPlaces(tickSize);

	useEffect(() => {
		const updateInputs = (
			inputSource: TpSlInputSource,
			setState: Dispatch<SetStateAction<TpSlOrderFormState>>,
			isTakeProfit: boolean
		) => {
			if (inputSource === 'price') {
				setState((prevState) => {
					const percent = prevState.price
						? calcPercent(prevState.price)
						: prevState.percent;
					const percentStringValue = percent
						? percent.toFixed(2)
						: prevState.percentStringValue;

					return {
						...prevState,
						percent,
						percentStringValue,
					} satisfies TpSlOrderFormState;
				});
			} else {
				setState((prevState) => {
					const price = prevState.percent
						? calcPrice(prevState.percent, isTakeProfit)
						: prevState.price;
					const priceStringValue = price
						? price.toFixed(tickSizeDecimalPlaces)
						: prevState.priceStringValue;

					return {
						...prevState,
						price,
						priceStringValue,
					} satisfies TpSlOrderFormState;
				});
			}
		};

		updateInputs(takeProfitInputSource, setTakeProfitState, true);
		updateInputs(stopLossInputSource, setStopLossState, false);
	}, [
		takeProfitInputSource,
		stopLossInputSource,
		entryPrice,
		calcPercent,
		calcPrice,
		tickSizeDecimalPlaces,
	]);

	////////////////////////////////////////////////////////////////////////////

	////////////////////////////////////////////////////////////////////////////
	// Validation Effects
	////////////////////////////////////////////////////////////////////////////

	// Prices are already validated on change in their respective handlers.
	// So we exclude their updates from triggering the below validation effects.
	const takeProfitPriceRef = useSyncedRef(takeProfitPrice);
	const stopLossPriceRef = useSyncedRef(stopLossPrice);
	const takeProfitLimitPriceRef = useSyncedRef(takeProfitLimitPrice);
	const stopLossLimitPriceRef = useSyncedRef(stopLossLimitPrice);

	// Validate take profit on mark price change.
	useEffect(() => {
		validatePrice(
			takeProfitPriceRef.current,
			'takeProfit',
			'market',
			direction,
			entryPrice,
			markPrice,
			allowTrailingStopLoss,
			setTakeProfitState
		);
	}, [direction, entryPrice, markPrice, allowTrailingStopLoss]);

	// Validate take profit limit price on mark price change.
	useEffect(() => {
		validatePrice(
			takeProfitLimitPriceRef.current,
			'takeProfit',
			'limit',
			direction,
			entryPrice,
			markPrice,
			allowTrailingStopLoss,
			setTakeProfitState
		);
	}, [direction, entryPrice, markPrice, allowTrailingStopLoss]);

	// Validate stop loss on mark price change.
	useEffect(() => {
		validatePrice(
			stopLossPriceRef.current,
			'stop',
			'market',
			direction,
			entryPrice,
			markPrice,
			allowTrailingStopLoss,
			setStopLossState
		);
	}, [direction, entryPrice, markPrice, allowTrailingStopLoss]);

	// Validate stop loss limit price on mark price change.
	useEffect(() => {
		validatePrice(
			stopLossLimitPriceRef.current,
			'stop',
			'limit',
			direction,
			entryPrice,
			markPrice,
			allowTrailingStopLoss,
			setStopLossState
		);
	}, [direction, entryPrice, markPrice, allowTrailingStopLoss]);
}

function validatePrice(
	price: BigNum | null,
	type: 'stop' | 'takeProfit',
	inputType: TpSlInputType,
	direction: 'long' | 'short',
	entryPrice: BigNum,
	markPrice: BigNum,
	allowTrailingStopLoss: boolean,
	setState: Dispatch<SetStateAction<TpSlOrderFormState>>
) {
	if (!price || price.eqZero()) {
		return;
	}

	const { invalid, reason } = validateTpSlOrder(
		price,
		type,
		direction,
		entryPrice,
		markPrice,
		allowTrailingStopLoss
	);

	setState((prevState) => {
		const newPriceInvalid =
			inputType === 'market' ? invalid : prevState.priceInvalid;
		const newPriceInvalidReason =
			inputType === 'market' ? reason : prevState.priceInvalidReason;
		const newLimitPriceInvalid =
			inputType === 'limit' ? invalid : prevState.limitPriceInvalid;
		const newLimitPriceInvalidReason =
			inputType === 'limit' ? reason : prevState.limitPriceInvalidReason;

		if (
			prevState.priceInvalid === newPriceInvalid &&
			prevState.priceInvalidReason === newPriceInvalidReason &&
			prevState.limitPriceInvalid === newLimitPriceInvalid &&
			prevState.limitPriceInvalidReason === newLimitPriceInvalidReason
		) {
			return prevState;
		}

		return {
			...prevState,
			priceInvalid: newPriceInvalid,
			priceInvalidReason: newPriceInvalidReason,
			limitPriceInvalid: newLimitPriceInvalid,
			limitPriceInvalidReason: newLimitPriceInvalidReason,
		} satisfies TpSlOrderFormState;
	});
}
